/**
 * Which notice the sign-in screen shows, and its words. Pure, so every state is tested
 * without a browser. Only states the hub and the browser really report are here:
 *
 *   offline          the browser says it has no network (navigator.onLine)
 *   unreachable      /api/me failed without an answer while the sign-in screen was up
 *   sign-in errors   the hub's GitHub, Google or Microsoft route redirected with
 *                    #signin-error=<code>&provider=<id> (SIGN_IN_ERROR_CODES; GitHub sends no
 *                    provider). Refusals of the account itself are "access denied"; the rest
 *                    say what failed, with a retry through the same provider
 *   link-rejected    the page was opened with #token=... and the hub refused that link
 *   session-ended    the hub stopped accepting this browser's session while it was signed in,
 *                    or ended its live stream because the session expired or was signed out
 *                    elsewhere (SSE auth-ended, SD-2)
 *   access-ended     the hub ended the live stream because this person was removed from the
 *                    hub or the sign-in was revoked
 *
 * A sign-out from the user menu shows no notice: the menu already confirms it with a toast.
 *
 * Words follow the house rules: what failed, why, what to do next; no em dashes. `hosted`: the
 * server is the hosted control plane (GET /api/auth/providers says server 'control-plane'), so
 * no notice points at a hub machine, a terminal, orgus up or orgus token.
 */
import type { SignInProviderId } from '@orgus/protocol';
import { SIGN_IN_PROVIDER_LABEL } from '@orgus/protocol';
import type { UiError } from '../../lib/api.ts';
import { signinErrorMessage } from './labels.ts';

/**
 * Why the sign-in screen is up, as AuthGate saw it happen. The last four come from the hub's
 * auth-ended frame on the live stream (SD-2); 'signed-out' is this browser's own sign-out.
 */
export type SignedOutReason = 'session-ended' | 'signed-out' | 'signed-out-elsewhere' | 'expired' | 'removed' | 'revoked';

export type SignInNoticeKind =
  | 'offline'
  | 'unreachable'
  | 'access-denied'
  | 'provider-error'
  | 'link-rejected'
  | 'rate-limited'
  | 'session-ended'
  | 'access-ended';

/** 'failed' and 'offline' are problems; 'neutral' is information. Drives icon and colour. */
export type SignInNoticeTone = 'failed' | 'offline' | 'neutral';

export interface SignInNotice {
  kind: SignInNoticeKind;
  tone: SignInNoticeTone;
  /** Short status label shown beside the icon (status is icon plus label). */
  label: string;
  what: string;
  why?: string;
  next?: string;
  /** The notice offers a "Try again" that asks the hub again (or starts the sign-in again). */
  retry?: boolean;
  /** The retry button's words, when not "Try again". */
  retryLabel?: string;
  /** A sign-in error: the provider to start again on retry, when the hub named one. */
  provider?: SignInProviderId | null;
  /** A refused account: the account the server named (a proven email or a GitHub login), else null. */
  account?: string | null;
  /** Errors interrupt (role alert); information waits its turn (role status). */
  urgent: boolean;
}

export interface SignInNoticeInput {
  host: string;
  online: boolean;
  /** The hub could not be asked who we are (useMe status 'error'). */
  hubError: UiError | null;
  /** From a provider's callback redirect (#signin-error=). No provider: the words fit any. */
  signinError: { code: string; login: string | null; provider?: SignInProviderId | null } | null;
  /** The owner link in the address bar was refused (the data layer's bootstrap error). */
  linkBootError: UiError | null;
  reason: SignedOutReason | null;
  /** The hosted control plane, not a hub on someone's machine. */
  hosted?: boolean;
}

const HUB_START = 'orgus up';
const NEW_LINK = 'orgus token';

/** A notice's title is a heading: no ending period (content.md), ellipses kept. */
export const headline = (text: string): string => text.replace(/(?<!\.)\.$/, '');

export function unreachableNotice(host: string, err: UiError | null, hosted = false): SignInNotice {
  // The hub answered, but with an error (a 5xx while it starts or stops): use its words.
  if (err && answered(err)) {
    return { kind: 'unreachable', tone: 'failed', label: 'Hub error', what: headline(err.what), why: err.why, next: err.next, retry: true, urgent: true };
  }
  if (hosted) {
    return {
      kind: 'unreachable',
      tone: 'failed',
      label: 'Orgus unreachable',
      what: `Couldn't reach Orgus at ${host}`,
      why: err?.why || 'The connection failed before Orgus answered.',
      next: 'Check your connection, then try again.',
      retry: true,
      urgent: true,
    };
  }
  return {
    kind: 'unreachable',
    tone: 'failed',
    label: 'Hub unreachable',
    what: `Couldn't reach the hub at ${host}`,
    why: err?.why || 'The connection failed before the hub answered.',
    next: `Check that the hub is running (start it with ${HUB_START}), then try again.`,
    retry: true,
    urgent: true,
  };
}

export function offlineNotice(host: string, hosted = false): SignInNotice {
  return {
    kind: 'offline',
    tone: 'offline',
    label: 'Offline',
    what: 'This device is offline',
    why: `Signing in needs a connection to ${hosted ? 'Orgus' : 'the hub'} at ${host}.`,
    next: 'Reconnect to the network. This page checks again by itself when you are back.',
    urgent: true,
  };
}

function linkRejected(err: UiError, hosted: boolean): SignInNotice {
  if (err.status === 429 || err.code === 'rate-limited') {
    return { kind: 'rate-limited', tone: 'failed', label: 'Too many attempts', what: headline(err.what), why: err.why, next: err.next, urgent: true };
  }
  // The control plane says exactly why (used, expired, off once an owner signs in with an account).
  if (hosted) return { kind: 'link-rejected', tone: 'neutral', label: 'Link not accepted', what: headline(err.what), why: err.why, next: err.next, urgent: true };
  return {
    kind: 'link-rejected',
    tone: 'neutral',
    label: 'Link expired',
    what: 'That sign-in link no longer works',
    why: 'Each start of the hub makes a new link, and older links stop working.',
    next: `Run ${NEW_LINK} for a new link, or sign in below.`,
    urgent: true,
  };
}

/** A command the user types, matched inside notice text so it can be set in mono. */
export const COMMAND = /(orgus(?: [a-z][a-z-]*)?)/;

/**
 * The server's answer when it refused an owner link: not valid (401 bad-token), too many tries
 * (429), or on the control plane used already, expired, or off once an owner signs in with an
 * account (used, expired, owner-link-off).
 */
const LINK_REFUSALS = new Set(['bad-token', 'rate-limited', 'used', 'expired', 'owner-link-off']);
export function isLinkRefusal(e: UiError | null | undefined): e is UiError {
  return !!e && ((!!e.code && LINK_REFUSALS.has(e.code)) || e.status === 429);
}

/** True for an error the hub answered (it has a status); false when nothing answered. */
const answered = (e: UiError | null | undefined): boolean => typeof e?.status === 'number';

/** The one notice to show above the sign-in methods, most urgent first; null for none. */
export function signInNotice(i: SignInNoticeInput): SignInNotice | null {
  const hosted = !!i.hosted;
  if (!i.online) return offlineNotice(i.host, hosted);
  if (i.hubError) return unreachableNotice(i.host, i.hubError, hosted);
  if (i.signinError) return signinErrorNotice(i.signinError, hosted);
  if (i.linkBootError) {
    return answered(i.linkBootError) ? linkRejected(i.linkBootError, hosted) : unreachableNotice(i.host, i.linkBootError, hosted);
  }
  const ended = i.reason ? streamEndedNotice(i.reason) : null;
  if (ended) return ended;
  if (i.reason === 'session-ended') {
    return {
      kind: 'session-ended',
      tone: 'neutral',
      label: 'Signed out',
      what: 'Your session ended',
      why: 'It expired or was ended elsewhere.',
      next: 'Sign in again.',
      urgent: false,
    };
  }
  return null;
}

/**
 * The hub ended the live stream and said why (SSE auth-ended, SD-2): one line, what happened and
 * what to do. Null for the reasons that are not a stream ending.
 */
export function streamEndedNotice(reason: SignedOutReason): SignInNotice | null {
  switch (reason) {
    case 'signed-out-elsewhere':
      return { kind: 'session-ended', tone: 'neutral', label: 'Signed out', what: 'This browser was signed out.', next: 'Sign in again to continue.', urgent: false };
    case 'expired':
      return { kind: 'session-ended', tone: 'neutral', label: 'Session expired', what: 'Your session expired.', next: 'Sign in again to continue.', urgent: false };
    case 'removed':
      return { kind: 'access-ended', tone: 'neutral', label: 'Access removed', what: 'You were removed from this hub.', next: 'Ask an admin of this hub to add you again.', urgent: false };
    case 'revoked':
      return { kind: 'access-ended', tone: 'neutral', label: 'Access revoked', what: 'Your sign-in was revoked.', next: 'Sign in again, or ask an admin of this hub why.', urgent: false };
    default:
      return null;
  }
}

/** Refusals of the account itself: it reached the hub, and the hub would not let it in. */
const ACCESS_DENIED = new Set(['not-allowed', 'domain-not-allowed', 'tenant-not-allowed']);

/**
 * Sign-ins that ran out of time or were cancelled: nothing is broken, so they read as
 * information in grey, not as a failure in red. Signing in again is the whole fix.
 */
const EXPIRED = new Set(['state', 'link-expired', 'denied']);

/** The status label beside the icon, per #signin-error code. */
export function signinErrorLabel(code: string, provider?: SignInProviderId | null): string {
  if (ACCESS_DENIED.has(code)) return 'No access';
  switch (code) {
    case 'denied':
      return 'Cancelled';
    case 'state':
      return 'Sign-in expired';
    case 'busy':
      return 'Too many sign-ins';
    case 'unreachable':
      return provider ? `${SIGN_IN_PROVIDER_LABEL[provider]} unreachable` : 'Provider unreachable';
    case 'email-unverified':
      return 'Email not verified';
    case 'not-configured':
      return 'Not set up';
    case 'reverify':
      return 'Sign-in paused';
    case 'invite-invalid':
      return 'Invite not valid';
    case 'invite-mismatch':
      return 'Wrong account';
    case 'already-member':
      return 'Already a member';
    case 'link-conflict':
      return 'Already linked';
    case 'link-expired':
      return 'Linking expired';
    case 'token-invalid':
      return 'Sign-in refused';
    case 'insecure':
      return 'Not encrypted';
    case 'bad-link':
      return 'Link not accepted';
    case 'rate-limited':
      return 'Too many attempts';
    default:
      return 'Sign-in failed';
  }
}

/**
 * A refused or failed sign-in from a provider's redirect. Every code but not-configured (the
 * provider is off, so starting it again cannot work) offers to start the same provider again:
 * after a cancel, an expired attempt or a refusal, that is also how to pick another account.
 */
export function signinErrorNotice(e: { code: string; login: string | null; provider?: SignInProviderId | null }, hosted = false): SignInNotice {
  const provider = e.provider ?? null;
  const m = signinErrorMessage(e.code, e.login, provider, hosted);
  const retry = provider != null && e.code !== 'not-configured';
  const denied = ACCESS_DENIED.has(e.code);
  // A refusal is not a fault: nothing broke, the account is just not let in. It reads in grey,
  // and its retry is the provider's account picker, since the same account would be refused again.
  // GitHub has no picker (it signs in whoever is signed in to github.com), so it only tries again.
  const picker = denied && provider !== 'github';
  return {
    kind: denied ? 'access-denied' : 'provider-error',
    tone: denied || EXPIRED.has(e.code) ? 'neutral' : 'failed',
    label: signinErrorLabel(e.code, provider),
    what: headline(m.what),
    why: m.why,
    next: m.next,
    ...(retry ? { retry: true, retryLabel: picker ? `Use another ${SIGN_IN_PROVIDER_LABEL[provider]} account` : `Try again with ${SIGN_IN_PROVIDER_LABEL[provider]}`, provider } : {}),
    ...(denied ? { account: e.login } : {}),
    urgent: true,
  };
}

/** The words for a failed owner-link sign-in from the form. */
export function linkErrorMessage(err: UiError, host: string, hosted = false): UiError {
  if (!answered(err)) {
    const n = unreachableNotice(host, err, hosted);
    return { what: n.what, why: n.why!, next: n.next! };
  }
  if (err.status === 429 || err.code === 'rate-limited') return err;
  // The control plane's refusal names the exact reason; a hub's 401 is always a stale link.
  if (hosted) return err;
  if (err.status === 401) {
    return {
      what: 'That sign-in link did not work.',
      why: 'It is not this hub\'s current link. Each start of the hub makes a new one, and older links stop working.',
      next: `Copy the newest link from the hub terminal, or run ${NEW_LINK} on the hub machine.`,
      status: 401,
    };
  }
  return err;
}

/** A failed link sign-in from the form, as a notice under the field. */
export function linkFailureNotice(err: UiError): SignInNotice {
  const label = !answered(err) ? 'Hub unreachable' : err.status === 429 || err.code === 'rate-limited' ? 'Too many attempts' : 'Link not accepted';
  const kind: SignInNoticeKind = !answered(err) ? 'unreachable' : err.status === 429 ? 'rate-limited' : 'link-rejected';
  return { kind, tone: 'failed', label, what: headline(err.what), why: err.why, next: err.next, urgent: true };
}

/** Validation for the link field before anything is sent. Null when it looks usable. */
export function checkLinkInput(text: string): string | null {
  const t = text.trim();
  if (!t) return 'Paste the sign-in link the hub printed. It ends in #token=.';
  if (/\s/.test(t)) return 'The link has spaces in it. Copy it again as one line.';
  return null;
}

/** The server is the hosted control plane: from the providers it reported, else as last seen here. */
export type ServerKind = 'hub' | 'control-plane';
const SERVER_KEY = 'orgus.server-kind';

/**
 * `reported`: the kind GET /api/auth/providers gave (a hub gives none: 'hub'), or null while it is
 * unknown. Remembered per browser, a convenience only, so the screen still says the right thing
 * when the server does not answer at all.
 */
export function serverKindOf(reported: ServerKind | null): ServerKind {
  try {
    if (reported) {
      localStorage.setItem(SERVER_KEY, reported);
      return reported;
    }
    return localStorage.getItem(SERVER_KEY) === 'control-plane' ? 'control-plane' : 'hub';
  } catch {
    return reported ?? 'hub';
  }
}

/** Whether the hub can be asked anything right now. */
export type HubState = 'ok' | 'offline' | 'unreachable';

/**
 * The hub's state for the sign-in screen. Anything but 'ok' turns the sign-in methods off (a
 * click could only fail); they come back by themselves when the browser is online again or the
 * hub answers a retry.
 */
export function hubStateOf(online: boolean, hubError: UiError | null): HubState {
  return !online ? 'offline' : hubError ? 'unreachable' : 'ok';
}
